import {useMemo, useState} from 'react'
import {PageHeader} from '../components/ui/page-header'
import {DataTable} from '../components/ui/data-table'
import {MonoLabel} from '../components/ui/mono-label'
import {UnderlineInput, UnderlineSelect} from '../components/ui/underline-field'
import {GLYPHS_A} from '../lib/lvs-font/glyphs-a'
import {GLYPHS_B} from '../lib/lvs-font/glyphs-b'
import {layoutText, type FontVariant, type GlyphPlacement} from '../lib/lvs-font/layout'
import {glyphTopY} from '../lib/lvs-font/glyph-bounds'
import {ANNOTATION} from '../lib/annotation-inks'
import {round4} from '../lib/utils'
import {HorizDim, VertDim} from '../components/ui/dimension-line'

// ─── SVG layout constants ────────────────────────────────────────────────────
// PAD_TOP leaves headroom above the capline so diacritic marks that extend
// past h=350 (carons like Č reach ~447, macrons ~420) remain visible.
const PAD_TOP = 120
const PAD_BOTTOM = 90 // room for two annotation rows + labels
const PAD_LEFT = 68 // room for h + d annotation
const PAD_RIGHT = 32

const ROW_S = 28 // px below baseline: s (glyph width) annotation
const ROW_A = 56 // px below baseline: a (spacing) annotation

// Per-glyph annotation styling: thin lines, small labels.
const DIM_STYLE = {tickHalf: 4.5, strokeWidth: 0.7, fontSize: 8.5} as const

// ─── Spacing row per glyph ───────────────────────────────────────────────────
interface SpacedGlyph {
    glyph: GlyphPlacement
    spacingToNext: number | null // scaled (output) units
}

const GRID_PATTERN = (
    <defs>
        <pattern
            id='grid'
            width={10}
            height={10}
            patternUnits='userSpaceOnUse'
        >
            <path
                d='M 10 0 L 0 0 0 10'
                fill='none'
                stroke={ANNOTATION.surfaceLow}
                strokeWidth={0.5}
            />
        </pattern>
    </defs>
)

// ─── Page ────────────────────────────────────────────────────────────────────
export function GlyphInspectorPage() {
    const [variant, setVariant] = useState<FontVariant>('A')
    const [h, setH] = useState(350)
    const [text, setText] = useState('Rīga 5 km')

    const k = h / 350
    const glyphMap = variant === 'A' ? GLYPHS_A : GLYPHS_B
    const strokeWidth = h / 7

    const layout = useMemo(() => layoutText(text, variant, h), [text, variant, h])

    const spaced = useMemo<SpacedGlyph[]>(
        () =>
            layout.glyphs.map((g, i) => {
                const next = layout.glyphs[i + 1]
                return {
                    glyph: g,
                    spacingToNext: next !== undefined ? next.x - (g.x + g.width) : null
                }
            }),
        [layout]
    )

    // SVG dimensions
    const contentW = Math.max(layout.totalWidth, 80)
    const svgW = PAD_LEFT + contentW + PAD_RIGHT
    const svgH = PAD_TOP + h + PAD_BOTTOM

    // Key y coordinates
    const baselineY = PAD_TOP + h
    const caplineY = PAD_TOP
    const originX = PAD_LEFT

    return (
        <div className='space-y-8'>
            {/* ── Header ── */}
            <PageHeader
                eyebrow='LVS 77-3:2016 / B pielikums'
                title='Glifu Inspektors'
            />

            {/* ── Controls ── */}
            <div className='flex flex-wrap gap-x-8 gap-y-5 items-end'>
                {/* Variant */}
                <div className='flex flex-col gap-1.5'>
                    <MonoLabel>Raksta veids</MonoLabel>
                    <UnderlineSelect
                        value={variant}
                        onChange={e => setVariant(e.target.value as FontVariant)}
                    >
                        <option value='A'>„A" raksts — Parastais</option>
                        <option value='B'>„B" raksts — Šaurais</option>
                    </UnderlineSelect>
                </div>

                {/* Height + k */}
                <div className='flex items-end gap-4'>
                    <div className='flex flex-col gap-1.5'>
                        <MonoLabel>h (px)</MonoLabel>
                        <UnderlineInput
                            type='number'
                            min={50}
                            max={700}
                            step={10}
                            value={h}
                            onChange={e => setH(Math.max(10, Number(e.target.value)))}
                            className='w-24'
                        />
                    </div>
                    <div className='flex flex-col gap-1.5 pb-1.5'>
                        <span className='font-mono text-[10px] tracking-[0.2em] uppercase text-foreground/50'>
                            k = h ÷ 350
                        </span>
                        <span className='font-mono text-sm text-primary tabular-nums'>
                            {k.toFixed(5)}
                        </span>
                    </div>
                </div>

                {/* Text */}
                <div className='flex flex-col gap-1.5 flex-1 min-w-[220px]'>
                    <MonoLabel>Teksts</MonoLabel>
                    <UnderlineInput
                        type='text'
                        value={text}
                        onChange={e => setText(e.target.value)}
                    />
                </div>
            </div>

            {/* ── SVG Canvas ── */}
            <div className='overflow-x-auto border border-foreground/10 bg-surface-container-highest'>
                <svg
                    width={svgW}
                    height={svgH}
                    style={{display: 'block'}}
                >
                    {GRID_PATTERN}

                    {/* Grid background */}
                    <rect
                        x={originX}
                        y={0}
                        width={contentW}
                        height={svgH}
                        fill='url(#grid)'
                    />

                    {/* Guide lines */}
                    {/* Capline (dotted): shows the target h height — use this
                        to check whether each glyph reaches it. */}
                    <line
                        x1={originX - 4}
                        y1={caplineY}
                        x2={originX + contentW + 4}
                        y2={caplineY}
                        stroke={ANNOTATION.dim}
                        strokeWidth={1}
                        strokeDasharray='1 3'
                        opacity={0.6}
                    />
                    <line
                        x1={originX - 4}
                        y1={baselineY}
                        x2={originX + contentW + 4}
                        y2={baselineY}
                        stroke={ANNOTATION.guide}
                        strokeWidth={0.8}
                    />
                    {/* Stroke-width guide (d = h/7) */}
                    <line
                        x1={originX - 4}
                        y1={baselineY - strokeWidth}
                        x2={originX - 4 + 4}
                        y2={baselineY - strokeWidth}
                        stroke={ANNOTATION.muted}
                        strokeWidth={0.5}
                        strokeDasharray='2 2'
                    />

                    {/* Global h annotation (left of canvas) */}
                    <VertDim
                        x={originX - 24}
                        y1={caplineY}
                        y2={baselineY}
                        label={`h = ${h}`}
                        clr={ANNOTATION.dim}
                        {...DIM_STYLE}
                    />

                    {/* Per-glyph h annotations — each glyph's actual top
                        height above the baseline, labelled with the value in
                        LVS units (at h=350). Lets you see at a glance which
                        glyphs reach the full cap height and which don't. */}
                    {spaced.map(({glyph}, i) => {
                        const topNorm = glyphTopY(glyphMap[glyph.char])
                        const topY = baselineY - topNorm * k
                        return (
                            <VertDim
                                key={`h-${i}-${glyph.char}`}
                                x={originX + glyph.x + glyph.width + 4}
                                y1={topY}
                                y2={baselineY}
                                label={`h ${round4(topNorm)}`}
                                clr={ANNOTATION.dim}
                                {...DIM_STYLE}
                            />
                        )
                    })}

                    {/* d annotation */}
                    <VertDim
                        x={originX - 8}
                        y1={baselineY - strokeWidth}
                        y2={baselineY}
                        label='d'
                        clr={ANNOTATION.muted}
                        {...DIM_STYLE}
                    />

                    {/* ── Glyph paths ── */}
                    {spaced.map(({glyph}, i) => {
                        const pathD = glyphMap[glyph.char]
                        if (!pathD) return null
                        return (
                            <path
                                key={`glyph-${i}-${glyph.char}`}
                                d={pathD}
                                transform={`translate(${originX + glyph.x} ${baselineY}) scale(${glyph.scale} ${-glyph.scale})`}
                                fill={ANNOTATION.glyph}
                                fillRule='evenodd'
                            />
                        )
                    })}

                    {/* Per-glyph vertical s guides — dotted lines at each
                        glyph's left and right edges, so you can visually
                        check the glyph's width boundaries. */}
                    {spaced.map(({glyph}, i) => {
                        const x1 = originX + glyph.x
                        const x2 = originX + glyph.x + glyph.width
                        return (
                            <g key={`sv-${i}-${glyph.char}`}>
                                <line
                                    x1={x1}
                                    y1={caplineY}
                                    x2={x1}
                                    y2={baselineY + ROW_S}
                                    stroke={ANNOTATION.dim}
                                    strokeWidth={1}
                                    strokeDasharray='1 3'
                                    opacity={0.6}
                                />
                                <line
                                    x1={x2}
                                    y1={caplineY}
                                    x2={x2}
                                    y2={baselineY + ROW_S}
                                    stroke={ANNOTATION.dim}
                                    strokeWidth={1}
                                    strokeDasharray='1 3'
                                    opacity={0.6}
                                />
                            </g>
                        )
                    })}

                    {/* ── s annotations (glyph widths) ── */}
                    {spaced.map(({glyph}, i) => (
                        <HorizDim
                            key={`s-${i}-${glyph.char}`}
                            x1={originX + glyph.x}
                            x2={originX + glyph.x + glyph.width}
                            y={baselineY + ROW_S}
                            label={`s ${round4(glyph.width / k)}`}
                            clr={ANNOTATION.dim}
                            {...DIM_STYLE}
                            minLabelWidth={6}
                        />
                    ))}

                    {/* ── a annotations (kerning gaps) ── */}
                    {spaced.map(({glyph, spacingToNext}, i) => {
                        if (spacingToNext === null) return null
                        const x1 = originX + glyph.x + glyph.width
                        const x2 = x1 + spacingToNext
                        const lvsA = spacingToNext / k
                        return (
                            <HorizDim
                                key={`a-${i}-${glyph.char}`}
                                x1={x1}
                                x2={x2}
                                y={baselineY + ROW_A}
                                label={`a ${round4(lvsA)}`}
                                clr={ANNOTATION.radius}
                                {...DIM_STYLE}
                                minLabelWidth={6}
                            />
                        )
                    })}

                    {/* ── Row labels on left ── */}
                    <text
                        x={originX - 4}
                        y={baselineY + ROW_S}
                        textAnchor='end'
                        dominantBaseline='middle'
                        fontSize={7.5}
                        fontFamily='ui-monospace, monospace'
                        fill={ANNOTATION.dim}
                        opacity={0.7}
                    >
                        s
                    </text>
                    <text
                        x={originX - 4}
                        y={baselineY + ROW_A}
                        textAnchor='end'
                        dominantBaseline='middle'
                        fontSize={7.5}
                        fontFamily='ui-monospace, monospace'
                        fill={ANNOTATION.radius}
                        opacity={0.7}
                    >
                        a
                    </text>
                </svg>
            </div>

            {/* ── Data table ── */}
            <DataTable>
                <DataTable.Head>
                    <DataTable.HeadCell>Simbols</DataTable.HeadCell>
                    <DataTable.HeadCell>x (px)</DataTable.HeadCell>
                    <DataTable.HeadCell className='text-primary'>s pie h=350</DataTable.HeadCell>
                    <DataTable.HeadCell>s pie h={h}</DataTable.HeadCell>
                    <DataTable.HeadCell className='text-primary'>h pie h=350</DataTable.HeadCell>
                    <DataTable.HeadCell style={{color: ANNOTATION.radius}}>
                        a→nākamais (h=350)
                    </DataTable.HeadCell>
                    <DataTable.HeadCell>a→nākamais (px)</DataTable.HeadCell>
                </DataTable.Head>
                <tbody>
                    {spaced.map(({glyph, spacingToNext}, i) => {
                        const lvsS = glyph.width / k
                        const lvsA = spacingToNext !== null ? spacingToNext / k : null
                        const isNegKern = lvsA !== null && lvsA < 0
                        const lvsH = glyphTopY(glyphMap[glyph.char])
                        return (
                            <DataTable.Row key={`row-${i}-${glyph.char}`}>
                                <DataTable.Cell className='text-foreground font-bold text-sm'>
                                    {glyph.char === ' ' ? '⎵' : glyph.char}
                                </DataTable.Cell>
                                <DataTable.Cell className='text-foreground/50 tabular-nums'>
                                    {round4(glyph.x)}
                                </DataTable.Cell>
                                <DataTable.Cell className='text-primary tabular-nums font-medium'>
                                    {round4(lvsS)}
                                </DataTable.Cell>
                                <DataTable.Cell className='text-foreground/50 tabular-nums'>
                                    {round4(glyph.width)}
                                </DataTable.Cell>
                                <DataTable.Cell className='text-primary tabular-nums font-medium'>
                                    {round4(lvsH)}
                                </DataTable.Cell>
                                <DataTable.Cell
                                    className={`tabular-nums font-medium ${isNegKern ? 'text-destructive' : ''}`}
                                    style={isNegKern ? undefined : {color: ANNOTATION.radius}}
                                >
                                    {lvsA !== null ? round4(lvsA) : '—'}
                                </DataTable.Cell>
                                <DataTable.Cell className='text-foreground/50 tabular-nums'>
                                    {spacingToNext !== null ? round4(spacingToNext) : '—'}
                                </DataTable.Cell>
                            </DataTable.Row>
                        )
                    })}
                </tbody>
                <DataTable.Foot>
                    <DataTable.Cell
                        colSpan={3}
                        className='text-foreground/40 tracking-widest uppercase'
                    >
                        Kopā
                    </DataTable.Cell>
                    <DataTable.Cell className='text-foreground/70 tabular-nums font-medium'>
                        {round4(layout.totalWidth)} px
                    </DataTable.Cell>
                    <DataTable.Cell className='text-foreground/40 tabular-nums'></DataTable.Cell>
                    <DataTable.Cell className='text-foreground/40 tabular-nums'>÷ k</DataTable.Cell>
                    <DataTable.Cell className='text-foreground/40 tabular-nums'>
                        = {round4(layout.totalWidth / k)} pie h=350
                    </DataTable.Cell>
                </DataTable.Foot>
            </DataTable>
        </div>
    )
}
